/* eslint-disable ember/no-classic-components, ember/no-computed-properties-in-native-classes, ember/require-computed-property-dependencies, ember/require-tagless-components */
// Extends From
import Component from '@ember/component';
// Ember Imports
import { service } from '@ember/service';
import { computed } from '@ember/object';
import { alias } from '@ember/object/computed';
import { isEmpty } from '@ember/utils';
// External Imports
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import { sortBy } from '@linc-technologies/linced-common/utils/arrays';
import { scaleOrdinal } from 'd3-scale';

export default class AttendanceSection extends Component {
  // Services
  @service
  dates;

  @service
  fetch;

  @service
  options;

  @service
  session;

  @service
  store;

  @service
  strings;

  @service
  terms;

  @service
  toaster;

  learner = null;
  summaryData = null;
  dayCodes = [];
  termData = null;

  @computed('session.school')
  get periods() {
    if (this.trackMovements) {
      return 'Days';
    }
    return this.options.schoolGet(
      this.session.school,
      'core:attendance:periodsLabel',
    );
  }

  Jurisdiction = Jurisdiction;

  @alias('session.school.jurisdiction')
  jurisdiction;

  @computed('jurisdiction')
  get trackMovements() {
    return this.jurisdiction !== Jurisdiction.NZ_MOE;
  }

  @computed('terms.terms.@each.year')
  get currentYearTerms() {
    return sortBy(this.terms.currentAcademicYearTerms, 'start');
  }

  @computed('dayCodes.[]')
  get byTruancyCode() {
    let data = [];
    let rawData = {};
    let halfDaysMap = [];
    let allDayCodes = this.dayCodes || [];
    let totalDayCodes = 0;
    let scheme = this.getColourScheme();
    // For each term this academic year, build up data to be charted.
    this.currentYearTerms.forEach((term) => {
      let currTermAttendance = {
        term: term.term,
        year: 0,
        present: 0,
        justified: 0,
        unjustified: 0,
        total: 0,
      };

      // Build up map of truancyCode -> count
      rawData = allDayCodes.reduce((data, dayCode) => {
        // Return early if dayCode is outside the current term
        if (dayCode.date < term.start || dayCode.date > term.end) {
          return data;
        }

        // Increment total dayCodes
        totalDayCodes++;

        let updateData = (key, value) => {
          data[key] = (data[key] || 0) + value;
        };

        if (dayCode.present > 0) {
          currTermAttendance.present += dayCode.presentWholeDays;
          updateData('P', dayCode.presentWholeDays);
        }
        if (dayCode.unjustifiablyAbsent > 0) {
          currTermAttendance.unjustified +=
            dayCode.unjustifiablyAbsentWholeDays;
          updateData('U', dayCode.unjustifiablyAbsentWholeDays);
        }
        if (dayCode.justifiedAbsent > 0) {
          currTermAttendance.justified += dayCode.justifiedAbsentWholeDays;
          updateData('J', dayCode.justifiedAbsentWholeDays);
        }

        return data;
      }, {});

      // Flip map into a ready-to-chart array and push it into the array of terms to return.
      let termData = this.mapData(rawData, totalDayCodes, 'truancy', scheme);
      data.push({
        term: term.term,
        data: termData,
      });
      currTermAttendance.total =
        currTermAttendance.justified +
        currTermAttendance.present +
        currTermAttendance.unjustified;
      // only add to the map if the term has data
      if (currTermAttendance.total > 0) {
        halfDaysMap.push(currTermAttendance);
      }
    });

    return { data, halfDaysMap };
  }

  mapData(rawData, total, type, scheme) {
    let data = [];
    total = total * 2;
    Object.keys(rawData).forEach((code) => {
      let label = this.getLabelForTruancyCode(code);
      let count = rawData[code];

      // Handle pluralization for 'days'
      let displayMeasure = this.periods;
      if (displayMeasure === 'Days' && count === 1) {
        displayMeasure = 'Day';
      }

      data.push({
        value: code,
        count,
        tooltip: `${label} (${code}): ${count} ${displayMeasure}`,
        color: this.getColour(code, scheme),
      });
    });

    if (isEmpty(data)) {
      data.push({
        value: 'No data',
        count: 1,
        tooltip: 'No data',
        color: '#bdbdbd',
      });
    }

    return data;
  }

  getColourScheme() {
    return scaleOrdinal([
      '#d62728',
      '#ff9896',
      '#9467bd',
      '#8c564b',
      '#e377c2',
      '#7f7f7f',
      '#bcbd22',
      '#17becf',
      '#aec7e8',
      '#ffbb78',
      '#98df8a',
      '#c5b0d5',
      '#c49c94',
      '#f7b6d2',
      '#c7c7c7',
      '#dbdb8d',
      '#9edae5',
    ]);
  }

  getLabelForTruancyCode(code) {
    switch (code) {
      case 'I':
      case 'P':
        return 'Present';
      case 'U':
        return 'Unjustified';
      case 'J':
        return 'Justified';
      case 'O':
        return 'Overseas Posting';
      default:
        return 'Unknown';
    }
  }

  getColour(code, scheme) {
    switch (code) {
      case 'P':
        return '#2ca02c';
      case 'J':
        return '#ff7f0e';
      case 'U':
        return '#d62728';
    }

    // Fall back to colour scheme.
    return scheme(code);
  }
}
